import { useCallback, useEffect, useState } from 'react';

// Column model for the Referrals table. Unlike the Screener/Ward tables (which
// share SCREENER_COLUMN_DEFS) the Referrals columns are bespoke — a colour
// percentile pill, a wrapping rationale, a signal-confidence band — so they get
// their own ordered/sized/visible config, persisted per panel. Mirrors the
// shape the shared ColumnsMenu / ColumnHeaderCell expect.

export const REFERRALS_COLUMN_IDS = [
  'signal',
  'sellerReadiness',
  'entity',
  'country',
  'region',
  'subsegment',
  'revenue',
  'revConf',
  'reason',
  'signalConf',
  'sellerConf',
] as const;
export type ReferralsColumnId = (typeof REFERRALS_COLUMN_IDS)[number];

interface ReferralsColumnDef {
  label: string;
  defaultWidth: number;
  numeric: boolean;
}

export const REFERRALS_COLUMN_DEFS: Record<ReferralsColumnId, ReferralsColumnDef> = {
  signal: { label: 'Origination Signal', defaultWidth: 116, numeric: false },
  sellerReadiness: { label: 'Seller readiness', defaultWidth: 96, numeric: false },
  entity: { label: 'Entity', defaultWidth: 210, numeric: false },
  country: { label: 'Country', defaultWidth: 64, numeric: false },
  region: { label: 'Region', defaultWidth: 130, numeric: false },
  subsegment: { label: 'Sub-segment', defaultWidth: 150, numeric: false },
  revenue: { label: 'Revenue est.', defaultWidth: 96, numeric: true },
  revConf: { label: 'Rev. conf.', defaultWidth: 70, numeric: true },
  reason: { label: 'Why referred', defaultWidth: 280, numeric: false },
  signalConf: { label: 'Origination conf.', defaultWidth: 116, numeric: false },
  sellerConf: { label: 'Readiness conf.', defaultWidth: 116, numeric: false },
};

const MIN_WIDTH = 44;
// v3: renamed Signal → "Origination Signal", added the "Readiness conf." column,
// renamed "Signal confidence" → "Origination conf.". Bumped so the new default
// order/labels apply — persisted older layouts reset once to pick them up.
const SCHEMA_VERSION = 3;

export interface ReferralsColumnState {
  id: ReferralsColumnId;
  width: number;
  visible: boolean;
}

export function defaultReferralsColumnState(): ReferralsColumnState[] {
  return REFERRALS_COLUMN_IDS.map((id) => ({
    id,
    width: REFERRALS_COLUMN_DEFS[id].defaultWidth,
    visible: true,
  }));
}

export interface ReferralsColumnConfig {
  columns: ReferralsColumnState[];
}

export interface ReferralsColumnActions {
  setColumnWidth: (id: ReferralsColumnId, px: number) => void;
  setColumnVisible: (id: ReferralsColumnId, visible: boolean) => void;
  moveColumn: (id: ReferralsColumnId, targetIndex: number) => void;
  resetToDefaults: () => void;
}

export function useReferralsColumns(
  storageKey: string,
): [ReferralsColumnConfig, ReferralsColumnActions] {
  const [config, setConfig] = useState<ReferralsColumnConfig>(() => load(storageKey));

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ v: SCHEMA_VERSION, columns: config.columns }),
      );
    } catch {
      /* non-critical */
    }
  }, [config, storageKey]);

  const setColumnWidth = useCallback((id: ReferralsColumnId, px: number) => {
    const width = Math.max(MIN_WIDTH, Math.round(px));
    setConfig((c) => ({
      columns: c.columns.map((col) => (col.id === id ? { ...col, width } : col)),
    }));
  }, []);

  const setColumnVisible = useCallback((id: ReferralsColumnId, visible: boolean) => {
    setConfig((c) => ({
      columns: c.columns.map((col) => (col.id === id ? { ...col, visible } : col)),
    }));
  }, []);

  const moveColumn = useCallback((id: ReferralsColumnId, targetIndex: number) => {
    setConfig((c) => {
      const fromIndex = c.columns.findIndex((col) => col.id === id);
      if (fromIndex === -1) return c;
      const clamped = Math.max(0, Math.min(c.columns.length - 1, targetIndex));
      if (fromIndex === clamped) return c;
      const next = [...c.columns];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return c;
      next.splice(clamped, 0, moved);
      return { columns: next };
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setConfig({ columns: defaultReferralsColumnState() });
  }, []);

  return [config, { setColumnWidth, setColumnVisible, moveColumn, resetToDefaults }];
}

function load(storageKey: string): ReferralsColumnConfig {
  const defaults: ReferralsColumnConfig = { columns: defaultReferralsColumnState() };
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as { v?: number; columns?: unknown };
    if (parsed.v !== SCHEMA_VERSION || !Array.isArray(parsed.columns)) return defaults;
    const known = new Set<ReferralsColumnId>(REFERRALS_COLUMN_IDS);
    const stored = (parsed.columns as unknown[]).filter(
      (c): c is ReferralsColumnState =>
        typeof c === 'object' &&
        c !== null &&
        known.has((c as ReferralsColumnState).id) &&
        typeof (c as ReferralsColumnState).width === 'number' &&
        typeof (c as ReferralsColumnState).visible === 'boolean',
    );
    const seen = new Set(stored.map((c) => c.id));
    const missing = REFERRALS_COLUMN_IDS.filter((id) => !seen.has(id)).map((id) => ({
      id,
      width: REFERRALS_COLUMN_DEFS[id].defaultWidth,
      visible: true,
    }));
    return { columns: [...stored, ...missing] };
  } catch {
    return defaults;
  }
}
